import * as React from 'react';
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  EditorConfig,
  // SerializedDecoratorNode is not exported in some Lexical builds; use base SerializedLexicalNode shape
  createCommand,
  $getSelection,
  $isRangeSelection,
  $getNodeByKey,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

export type CitationField = 'number' | 'short' | 'full' | 'title';

export interface CitationPayload {
  citationId: string;
  field: CitationField;
  pinCite?: string | null;
  model?: 'bluebook' | 'custom';
  href?: string | null;
  textFallback?: string | null;
}

export type SerializedCitationNode = {
  type: 'citation';
  version: 1;
  payload: CitationPayload;
  // Minimal base fields from SerializedLexicalNode to satisfy typings
  format?: number;
  indent?: number;
  direction?: 'ltr' | 'rtl' | null;
};

export const INSERT_CITATION_COMMAND = createCommand<CitationPayload>('INSERT_CITATION_COMMAND');
export const UPDATE_CITATION_COMMAND = createCommand<{ key: NodeKey; patch: Partial<CitationPayload> }>(
  'UPDATE_CITATION_COMMAND'
);

export interface CitationResolver {
  format(payload: CitationPayload): { text: string; href?: string | null };
}
const CitationResolverContext = React.createContext<CitationResolver | null>(null);
export const CitationResolverProvider = CitationResolverContext.Provider;

function useCitationResolver(): CitationResolver | null {
  return React.useContext(CitationResolverContext);
}

function CitationInline({ payload }: { payload: CitationPayload }) {
  const resolver = useCitationResolver();
  const { text, href } = React.useMemo(() => {
    try {
      if (resolver) return resolver.format(payload);
    } catch {
      // ignore
    }
    return { text: payload.textFallback ?? `[cite:${payload.citationId}:${payload.field}]`, href: payload.href ?? null };
  }, [resolver, payload]);

  const inner = (
    <span
      className="edg-citation"
      data-citation-id={payload.citationId}
      data-citation-field={payload.field}
      title={text}
      style={{ textDecoration: 'underline dotted', cursor: 'pointer' }}
    >
      {text}
      {payload.pinCite ? `, ${payload.pinCite}` : ''}
    </span>
  );

  return href ? (
    <a href={href} rel="noopener noreferrer" target="_blank" className="edg-citation-link">
      {inner}
    </a>
  ) : (
    inner
  );
}

export class CitationNode extends DecoratorNode<React.ReactElement> {
  __payload: CitationPayload;

  static getType(): string {
    return 'citation';
  }

  static clone(node: CitationNode): CitationNode {
    return new CitationNode({ ...node.__payload }, node.__key);
  }

  constructor(payload: CitationPayload, key?: NodeKey) {
    super(key);
    this.__payload = payload;
  }

  isInline(): boolean { return true; }
  canInsertTextBefore(): boolean { return true; }
  canInsertTextAfter(): boolean { return true; }

  getTextContent(): string {
    const p = this.getPayload();
    const pin = p.pinCite ? `, ${p.pinCite}` : '';
    return p.textFallback ?? `[cite:${p.citationId}:${p.field}${pin}]`;
  }

  exportJSON(): SerializedCitationNode {
    return {
      ...super.exportJSON(),
      type: 'citation',
      version: 1,
      payload: this.getPayload(),
    };
  }

  static importJSON(json: SerializedCitationNode): CitationNode {
    return new CitationNode(json.payload);
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    span.className = 'lexical-citation-wrapper';
    span.style.display = 'inline';
    span.setAttribute('data-lexical-citation', 'true');
    return span;
  }

  updateDOM(): boolean { return false; }

  decorate(): React.ReactElement { return <CitationInline payload={this.getPayload()} />; }

  getPayload(): CitationPayload {
    const self = this.getLatest() as CitationNode;
    return self.__payload;
  }

  setPayload(patch: Partial<CitationPayload>) {
    const writable = this.getWritable();
    writable.__payload = { ...writable.__payload, ...patch };
  }
}

export function $createCitationNode(payload: CitationPayload): CitationNode { return new CitationNode(payload); }
export function $isCitationNode(node?: LexicalNode | null): node is CitationNode { return node instanceof CitationNode; }

export function $insertCitation(payload: CitationPayload): CitationNode | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;
  const node = $createCitationNode(payload);
  selection.insertNodes([node]);
  selection.insertText(' ');
  return node;
}

export function CitationsPlugin() {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    return editor.registerCommand(
      INSERT_CITATION_COMMAND,
      (payload) => {
        editor.update(() => { $insertCitation(payload); });
        return true;
      },
      0
    );
  }, [editor]);

  React.useEffect(() => {
    return editor.registerCommand(
      UPDATE_CITATION_COMMAND,
      ({ key, patch }) => {
        editor.update(() => {
          const n = $getNodeByKey(key);
          if ($isCitationNode(n)) n.setPayload(patch);
        });
        return true;
      },
      0
    );
  }, [editor]);

  return null;
}


